// =============================================================================
// FILE: packages/renderer/src/stores/usage.store.ts
// PURPOSE: Tracks meaningful in-app interactions so upgrade prompts are shown
//          only after the user has experienced core value — never during
//          onboarding or initial exploration.
//
//   Aha moment = any ONE of:
//     • First project scan completed (Smart Ingest knowledge graph)
//     • First project created
//     • 5+ distinct pages visited
// =============================================================================

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'r2.usage';

interface UsageState {
  pagesVisited:       string[];   // unique route ids
  ingestsCompleted:   number;     // Smart Ingest scans finished
  projectsCreated:    number;
  reportsViewed:      number;
  ahaMomentReached:   boolean;    // latched true, never reset
}

const DEFAULTS: UsageState = {
  pagesVisited:     [],
  ingestsCompleted: 0,
  projectsCreated:  0,
  reportsViewed:    0,
  ahaMomentReached: false,
};

function load(): UsageState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(s: UsageState): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* quota */ }
}

function check(s: UsageState): UsageState {
  if (s.ahaMomentReached) return s;
  const reached =
    s.ingestsCompleted >= 1 ||
    s.projectsCreated  >= 1 ||
    s.pagesVisited.length >= 5;
  return reached ? { ...s, ahaMomentReached: true } : s;
}

const subs = new Set<() => void>();
let state: UsageState = check(load());
function notify(): void { for (const fn of subs) fn(); }

export const usageStore = {
  getState: (): UsageState => state,

  recordPageVisit(routeId: string): void {
    if (state.pagesVisited.includes(routeId)) return;
    state = check({ ...state, pagesVisited: [...state.pagesVisited, routeId] });
    save(state); notify();
  },

  recordIngestComplete(): void {
    state = check({ ...state, ingestsCompleted: state.ingestsCompleted + 1 });
    save(state); notify();
  },

  recordProjectCreated(): void {
    state = check({ ...state, projectsCreated: state.projectsCreated + 1 });
    save(state); notify();
  },

  recordReportViewed(): void {
    state = check({ ...state, reportsViewed: state.reportsViewed + 1 });
    save(state); notify();
  },

  subscribe(fn: () => void): () => void {
    subs.add(fn); return () => subs.delete(fn);
  },
};

export function useUsageStore(): UsageState {
  return useSyncExternalStore(usageStore.subscribe, usageStore.getState, usageStore.getState);
}
